// OccoPay Design System
// Professional Payment Service UI Design System

export const DesignSystem = {
  // Color Palette
  colors: {
    // Primary Colors - Main Green
    primary: {
      50: '#f7fee7',
      100: '#ecfccb',
      200: '#d9f99d',
      300: '#bef264',
      400: '#a3e635',
      500: '#84cc16',
      600: '#65a30d',
      700: '#4d7c0f',
      800: '#3f6212',
      900: '#365314',
    },

    // Secondary Colors - Success Green
    secondary: {
      50: '#f0fdf4',
      100: '#dcfce7',
      200: '#bbf7d0',
      300: '#86efac',
      400: '#4ade80',
      500: '#22c55e',
      600: '#16a34a',
      700: '#15803d',
      800: '#166534',
      900: '#14532d',
    },

    // Success Colors
    success: {
      50: '#f0fdf4',
      100: '#dcfce7',
      200: '#bbf7d0',
      300: '#86efac',
      400: '#4ade80',
      500: '#22c55e',
      600: '#16a34a',
      700: '#15803d',
      800: '#166534',
      900: '#14532d',
    },

    // Warning Colors
    warning: {
      50: '#fffbeb',
      100: '#fef3c7',
      200: '#fde68a',
      300: '#fcd34d',
      400: '#fbbf24',
      500: '#f59e0b',
      600: '#d97706',
      700: '#b45309',
      800: '#92400e',
      900: '#78350f',
    },

    // Error Colors
    error: {
      50: '#fef2f2',
      100: '#fee2e2',
      200: '#fecaca',
      300: '#fca5a5',
      400: '#f87171',
      500: '#ef4444',
      600: '#dc2626',
      700: '#b91c1c',
      800: '#991b1b',
      900: '#7f1d1d',
    },

    // Neutral Colors
    neutral: {
      50: '#f8fafc',
      100: '#f1f5f9',
      200: '#e2e8f0',
      300: '#cbd5e1',
      400: '#94a3b8',
      500: '#64748b',
      600: '#475569',
      700: '#334155',
      800: '#1e293b',
      900: '#0f172a',
    },

    // Background Colors
    background: {
      default: '#f8fafc',
      paper: '#ffffff',
      card: '#ffffff',
      sidebar: '#1e293b',
    },

    // Text Colors
    text: {
      primary: '#1e293b',
      secondary: '#64748b',
      disabled: '#94a3b8',
      inverse: '#ffffff',
    },
  },

  // Typography
  typography: {
    fontFamily: {
      primary: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      mono: '"SF Mono", "Monaco", "Inconsolata", "Roboto Mono", monospace',
    },

    fontSize: {
      xs: '0.75rem',
      sm: '0.875rem',
      base: '1rem',
      lg: '1.125rem',
      xl: '1.25rem',
      '2xl': '1.5rem',
      '3xl': '1.875rem',
      '4xl': '2.25rem',
      '5xl': '3rem',
      '6xl': '3.75rem',
    },

    fontWeight: {
      light: 300,
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
      extrabold: 800,
    },

    lineHeight: {
      tight: 1.25,
      normal: 1.5,
      relaxed: 1.75,
    },
  },

  // Spacing
  spacing: {
    0: '0',
    1: '0.25rem',
    2: '0.5rem',
    3: '0.75rem',
    4: '1rem',
    5: '1.25rem',
    6: '1.5rem',
    8: '2rem',
    10: '2.5rem',
    12: '3rem',
    16: '4rem',
    20: '5rem',
    24: '6rem',
    32: '8rem',
    40: '10rem',
    48: '12rem',
    56: '14rem',
    64: '16rem',
  },

  // Border Radius
  borderRadius: {
    none: '0',
    sm: '0.125rem',
    base: '0.25rem',
    md: '0.375rem',
    lg: '0.5rem',
    xl: '0.75rem',
    '2xl': '1rem',
    '3xl': '1.5rem',
    full: '9999px',
  },

  // Shadows
  shadows: {
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    base: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    '2xl': '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    inner: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.06)',
    none: 'none',
  },

  // Transitions
  transitions: {
    fast: '150ms ease-in-out',
    normal: '250ms ease-in-out',
    slow: '350ms ease-in-out',
  },

  // Component Specific Styles
  components: {
    // Button Styles
    button: {
      primary: {
        backgroundColor: '#6c9c28',
        color: '#ffffff',
        border: 'none',
        borderRadius: '8px',
        padding: '12px 24px',
        fontWeight: 600,
        fontSize: '0.875rem',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          backgroundColor: '#65a30d',
          transform: 'translateY(-1px)',
          boxShadow: '0 4px 12px rgba(108, 156, 40, 0.3)',
        },
      },
      secondary: {
        backgroundColor: 'transparent',
        color: '#6c9c28',
        border: '2px solid #6c9c28',
        borderRadius: '8px',
        padding: '10px 22px',
        fontWeight: 600,
        fontSize: '0.875rem',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          backgroundColor: '#6c9c28',
          color: '#ffffff',
          transform: 'translateY(-1px)',
        },
      },
      success: {
        backgroundColor: '#059669',
        color: '#ffffff',
        border: 'none',
        borderRadius: '8px',
        padding: '12px 24px',
        fontWeight: 600,
        fontSize: '0.875rem',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          backgroundColor: '#047857',
          transform: 'translateY(-1px)',
          boxShadow: '0 4px 12px rgba(5, 150, 105, 0.3)',
        },
      },
    },

    // Card Styles
    card: {
      default: {
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24)',
        border: '1px solid #e2e8f0',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.15), 0 2px 4px rgba(0, 0, 0, 0.12)',
          transform: 'translateY(-2px)',
        },
      },
      elevated: {
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
        border: 'none',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          transform: 'translateY(-4px)',
        },
      },
    },

    // Status Colors for Payment States
    status: {
      pending: '#d97706', // Warning orange
      completed: '#059669', // Success green
      failed: '#dc2626', // Error red
      cancelled: '#64748b', // Neutral gray
      processing: '#3b82f6', // Info blue
      refunded: '#8b5cf6', // Purple
      disputed: '#f59e0b', // Warning yellow
    },

    // Layout & Page Styles
    layout: {
      // Page Container - Prevents content from being hidden behind fixed topbar
      pageContainer: {
        padding: '16px 24px',
        paddingTop: '80px', // Adjust based on your topbar height
        backgroundColor: '#fafafa',
        minHeight: '100vh',
      },

      // Page Header
      pageHeader: {
        display: 'flex',
        alignItems: 'center',
        marginBottom: '24px',
        backgroundColor: 'white',
        padding: '16px',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
      },

      // Content Card
      contentCard: {
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
        overflow: 'hidden',
      },

      // Back Button
      backButton: {
        backgroundColor: '#f3f4f6',
        marginRight: '16px',
        borderRadius: '8px',
      },

      // Page Title
      pageTitle: {
        margin: 0,
        fontSize: '24px',
        fontWeight: 600,
        color: '#1f2937',
        flex: 1,
      },
    },
  },
};

// Payment Status Color Mapping
export const PaymentStatusColors = {
  0: DesignSystem.components.status.pending, // PRE_AUTH_OR_WAITING_PAYMENT
  1: DesignSystem.components.status.completed, // COMPLETED
  2: DesignSystem.components.status.pending, // TIMEOUT
  3: DesignSystem.components.status.failed, // FAILED
  4: DesignSystem.components.status.pending, // EMPTY
  5: DesignSystem.components.status.cancelled, // CANCELLED_OR_RETURNED
  6: DesignSystem.components.status.processing, // WAITING_APPROVAL_CANCEL_OR_RETURN
  7: DesignSystem.components.status.failed, // REJECTED_CANCEL_OR_RETURN
  8: DesignSystem.components.status.refunded, // PARTIAL_RETURNED_OR_CANCELED
  20: DesignSystem.components.status.pending, // PARTIAL_RETURNED_OR_CANCELED
  30: DesignSystem.components.status.processing, // PARTIAL_RETURNED_OR_CANCELED
};

export default DesignSystem;
